/**
 * Automation (Nova): shared DSL contract for the plain-English → planner →
 * executor durable automation builder (M4, Pro-only).
 *
 * A plain sentence is compiled by the planner (AgentAiService.generateStructured)
 * into a versioned `NovaAutomation` JSON DSL: one trigger + ordered steps (each
 * with per-step safety) + outputs + limits + metadata. The DSL is validated
 * server-side against a fixed JSON Schema + policy (see `@tabbio/utils`
 * automation/nova-plan) and stored as an immutable version. The planner emits
 * JSON ONLY and never executes: the executor (Trigger.dev tasks) is M4.2.
 *
 * Mirrors the Prisma models in `packages/db/prisma/schema/automation.prisma`.
 *
 * Runtime const arrays (NOVA_TRIGGER_TYPES, …) are re-exported through
 * `automation.runtime.js` so nodenext ESM consumers resolve them: import the
 * values from `@tabbio/types` (which points at the `.runtime.js`) and the types
 * from here.
 */

import type { AgentChatCapabilities } from './agent';

// ── Enumerations (const arrays back the unions; see automation.runtime.js) ─────

export const NOVA_TRIGGER_TYPES = ['cron', 'event', 'condition'] as const;
export type NovaTriggerType = (typeof NOVA_TRIGGER_TYPES)[number];

export const NOVA_AUTOMATION_STATUSES = [
  'draft',
  'active',
  'paused',
  'archived',
] as const;
export type NovaAutomationStatus = (typeof NOVA_AUTOMATION_STATUSES)[number];

export const NOVA_AUTOMATION_SOURCES = ['custom', 'template'] as const;
export type NovaAutomationSource = (typeof NOVA_AUTOMATION_SOURCES)[number];

export const NOVA_RUN_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'skipped',
  'killed',
  'awaiting_approval',
] as const;
export type NovaRunStatus = (typeof NOVA_RUN_STATUSES)[number];

export const NOVA_RUN_STEP_STATUSES = [
  'pending',
  'running',
  'succeeded',
  'failed',
  'skipped',
  'awaiting_approval',
] as const;
export type NovaRunStepStatus = (typeof NOVA_RUN_STEP_STATUSES)[number];

export const NOVA_AUDIT_ACTIONS = [
  'created',
  'edited',
  'activated',
  'paused',
  'resumed',
  'deleted',
  'approved',
  'killed',
] as const;
export type NovaAuditAction = (typeof NOVA_AUDIT_ACTIONS)[number];

/**
 * The three canonical step KIND prefixes. A step's `type` is either the literal
 * `'connector.action'` (with `connector` + `action` in `params`, bound to the
 * live connector catalog) or `'nova.<verb>'` / `'tabbio.<verb>'` (first-party
 * steps). The validator default-DENIES anything else.
 */
export const NOVA_STEP_KINDS = ['connector.action', 'nova', 'tabbio'] as const;
export type NovaStepKind = (typeof NOVA_STEP_KINDS)[number];

// ── Trigger ───────────────────────────────────────────────────────────────────

/** `cron` trigger params: schedule on a cron expression in an IANA timezone. */
export interface NovaCronTriggerParams {
  cron_expression: string;
  timezone: string;
  start_date?: string | null;
  end_date?: string | null;
}

/** `event` trigger params: fire on a provider event (source = catalog slug). */
export interface NovaEventTriggerParams {
  /** A connector slug from the LIVE catalog (planner cannot invent a service). */
  source: string;
  /** A `supportedTriggerTypes` value of `source`. */
  event_type: string;
  filters?: Record<string, unknown>;
}

/** `condition` trigger params: poll on a cron and fire when a condition holds. */
export interface NovaConditionTriggerParams {
  poll_cron_expression: string;
  condition_expression: string;
}

export type NovaTriggerParams =
  | NovaCronTriggerParams
  | NovaEventTriggerParams
  | NovaConditionTriggerParams;

export interface NovaTrigger {
  type: NovaTriggerType;
  params: NovaTriggerParams;
}

// ── Step ────────────────────────────────────────────────────────────────────

/**
 * Per-step safety. Phase-1 policy: EVERY side-effecting step MUST default to
 * `dry_run: true` + `requires_approval: true` (draft, never submit). Auto-submit
 * is Phase 2; the "I trust this" toggle is display-only in Phase 1.
 */
export interface NovaStepSafety {
  dry_run: boolean;
  requires_approval: boolean;
}

export interface NovaStepRetry {
  max_attempts: number;
  initial_delay_seconds: number;
  backoff_factor: number;
}

export interface NovaStep {
  id: string;
  /** `'connector.action'` | `'nova.<verb>'` | `'tabbio.<verb>'`. */
  type: string;
  params: Record<string, unknown>;
  safety: NovaStepSafety;
  depends_on: string[];
  retry?: NovaStepRetry | null;
}

// ── Output ────────────────────────────────────────────────────────────────────

export interface NovaOutput {
  id: string;
  /** e.g. `'tabbio.inbox'` | `'digest'` | `'notification'`. */
  type: string;
  destination?: string | null;
  params?: Record<string, unknown>;
}

// ── Metadata + limits ─────────────────────────────────────────────────────────

export interface NovaAutomationMetadata {
  automation_id: string;
  version: number;
  owner_user_id: string;
  status: NovaAutomationStatus;
}

export interface NovaAutomationLimits {
  max_runs_per_day: number;
  max_concurrent_runs: number;
}

// ── The DSL ────────────────────────────────────────────────────────────────────

/** The full, versioned automation DSL. One trigger; ordered steps; outputs. */
export interface NovaAutomation {
  /** Display name for the board card. */
  name?: string;
  /** One-line NL summary of what the automation does. */
  summary?: string;
  trigger: NovaTrigger;
  steps: NovaStep[];
  outputs: NovaOutput[];
  metadata: NovaAutomationMetadata;
  limits: NovaAutomationLimits;
}

/** The planner-emitted draft (server attaches `metadata` afterwards). */
export type NovaAutomationPlanDraft = Omit<NovaAutomation, 'metadata'>;

// ── Compile endpoint DTOs (POST /api/automation/compile) ───────────────────────

export interface NovaCompileRequest {
  sentence: string;
  locale?: string;
  scheduleHint?: string;
  /**
   * The composer's Research, Image and Result controls, the same shape as
   * `AgentChatRequest.capabilities`. Omitted fields default to `auto`; the
   * planner resolves them against what the user may use.
   */
  capabilities?: AgentChatCapabilities;
  /** The device's IANA timezone; the schedule uses it unless the sentence names another. */
  timezone?: string;
}

// ── Research and report steps (Tasks parity) ──────────────────────────────────

/** How a `nova.compose_report` step shapes its result. */
export type NovaReportFormat = 'summary' | 'document' | 'slides';

/** How far back a `nova.research_web` step looks. */
export type NovaResearchTimeWindow = 'day' | 'week' | 'month';

/** Files a `nova.render_document` step writes. HTML is always included. */
export type NovaRenderFormat = 'html' | 'pdf' | 'pptx';

/** Where a `nova.deliver` step sends the result. */
export type NovaDeliverChannel = 'in_app' | 'email';

/**
 * The capability decision a research plan carries on its `report` output
 * (`outputs[].params`): whether it reads the web or the user's sources, whether
 * it makes an image, and the result format.
 */
export interface NovaCapabilityDecision {
  research: 'on' | 'off';
  image: 'on' | 'off';
  format: NovaReportFormat;
}

/** One source a research plan reads, as shown in the plan preview. */
export type NovaReportSourceScope =
  | {
      kind: 'web';
      topic: string;
      region?: string | null;
      timeWindow: NovaResearchTimeWindow;
      maxSources: number;
    }
  | { kind: 'links'; urls: string[] }
  | { kind: 'pdf'; count: number }
  | { kind: 'github'; repo: string }
  | { kind: 'skill'; skillId: string };

/**
 * The research part of a plan preview: what it reads, what it makes, where it
 * sends it, and the estimated credits of one run from the catalog estimates.
 */
export interface NovaReportPreview {
  sources: NovaReportSourceScope[];
  format: NovaReportFormat;
  /** Rendered files; empty for a summary. */
  formats: NovaRenderFormat[];
  includeImage: boolean;
  channels: NovaDeliverChannel[];
  estimatedCredits: number;
  creditBreakdown: {
    run: number;
    research: number;
    image: number;
    render: number;
  };
}

/** A template input the user fills in before the template is enabled. */
export interface NovaTemplateInputSpec {
  key: 'topic' | 'region' | 'url' | 'asset_id';
  kind: 'text' | 'url' | 'pdf_asset';
  required: boolean;
}

/** `POST /api/automation/templates/:key/enable` body `inputs`. */
export interface NovaTemplateInputs {
  topic?: string;
  region?: string;
  url?: string;
  asset_id?: string;
}

/**
 * What a research run delivered, stored in the `nova.deliver` step data as
 * `report`. Titles and domains only: never a signed URL.
 */
export interface NovaRunReport {
  title: string;
  summary: string | null;
  format: NovaReportFormat;
  formats: NovaRenderFormat[];
  artifactIds: string[];
  sources: Array<{ title: string; domain: string }>;
  sourceCount: number;
  channels: NovaDeliverChannel[];
  /** A summary with nothing new since the previous run: delivered quietly. */
  quiet: boolean;
  locale: string;
}

/** A single step rendered for the plan-preview panel. */
export interface NovaStepPreview {
  id: string;
  type: string;
  label: string;
  dryRun: boolean;
  requiresApproval: boolean;
  sideEffecting: boolean;
}

/** A UI-friendly breakdown of the compiled plan (NL summary + structured). */
export interface NovaStructuredPreview {
  trigger: {
    type: NovaTriggerType;
    description: string;
  };
  steps: NovaStepPreview[];
  outputs: Array<{ id: string; type: string; description: string }>;
  limits: NovaAutomationLimits;
  /** Present for research plans: sources scope, formats, image, credits. */
  report?: NovaReportPreview | null;
}

export interface NovaCompileResult {
  automationId: string;
  versionId: string;
  dsl: NovaAutomation;
  summary: string;
  structuredPreview: NovaStructuredPreview;
}

// ── Board / run seam DTOs (read by M4.2) ───────────────────────────────────────

export interface NovaAutomationSummary {
  id: string;
  name: string;
  description?: string | null;
  status: NovaAutomationStatus | string;
  source: NovaAutomationSource | string;
  templateKey?: string | null;
  activeVersionId?: string | null;
  /**
   * The active version's cron expression: drives the board card's cadence
   * overline (DAILY / WEEKLY / …) and schedule line.
   */
  cron?: string | null;
  /** Next scheduled fire time (active cron triggers): drives the board card. */
  nextRunAt?: string | Date | null;
  /** The most recent run's outcome + when: shown as a "Last run …" line. */
  lastRunAt?: string | Date | null;
  lastRunStatus?: string | null;
  /**
   * `metrics.matchesFound` of that latest run when it recorded one: lets the
   * board say "no new matches" for a quiet succeeded run.
   */
  lastRunMatchesFound?: number | null;
  /**
   * That latest run's `errorSummary`: for a skipped run, the reason code
   * (entitlement, rate_automation, ...) the board turns into readable copy.
   */
  lastRunErrorSummary?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface NovaRunSummary {
  id: string;
  automationId: string;
  versionId: string;
  triggerType: NovaTriggerType | string;
  status: NovaRunStatus | string;
  scheduledTime?: string | Date | null;
  startTime?: string | Date | null;
  endTime?: string | Date | null;
  errorSummary?: string | null;
  metrics: Record<string, unknown>;
  createdAt: string | Date;
}

/**
 * A run paused awaiting the user's approval, surfaced across ALL their automations
 * so the board can show a single "Needs your approval" inbox with inline actions.
 */
export interface NovaPendingApproval {
  runId: string;
  automationId: string;
  automationName: string;
  /** The step waiting for approval (e.g. nova.draft_applications). */
  stepType?: string | null;
  /** Count of items the draft covers (jobs found, etc.) when known. */
  itemCount?: number | null;
  createdAt: string | Date;
}

// ── Run results (what a run actually produced) ─────────────────────────────────

/**
 * A concrete job match a run produced: persisted in the search step's
 * `NovaRunStep.data.matches` so the run panel / history / result email can show
 * the real jobs instead of a bare count.
 */
export interface NovaRunMatchItem {
  candidateId: string;
  catalogJobId: string;
  jobTitle: string;
  companyName: string;
  location?: string | null;
  matchScore?: number | null;
  matchReasons: string[];
  applyMode?: string | null;
  /** JobMatchCandidate status: matched | needs_review | eligible_auto_apply | … */
  status: string;
}

/** The composed digest a `compose_digest` step stores in its step data. */
export interface NovaRunDigest {
  title: string;
  itemCount: number;
  items: NovaRunMatchItem[];
  generatedAt: string;
}

// ── Generic run-result presentation ──────────────────────────────────────────

/**
 * A small, display-safe value shown at the top of an automation result. Step
 * handlers may persist these under `NovaRunStep.data.presentation`; clients
 * intentionally ignore arbitrary raw step JSON.
 */
export interface NovaRunResultStat {
  id: string;
  label: string;
  value: string | number;
}

/** A generalized result row. It is informative by default, not executable. */
export interface NovaRunResultItem {
  id: string;
  title: string;
  subtitle?: string | null;
  body?: string | null;
  badge?: string | null;
}

/** One visually grouped block in a generalized automation result. */
export interface NovaRunResultSection {
  id: string;
  title?: string | null;
  body?: string | null;
  items?: NovaRunResultItem[];
}

/**
 * Stable rendering contract for every present and future automation result.
 * Domain-specific result components (such as job matches) can render alongside
 * this document, while unknown raw provider/AI output never reaches the UI.
 */
export interface NovaRunResultPresentation {
  title?: string | null;
  summary?: string | null;
  stats?: NovaRunResultStat[];
  sections?: NovaRunResultSection[];
}

/**
 * A step from the run's version DSL, returned with run detail so the client can
 * render the FULL checklist (pending steps included) before their rows exist.
 */
export interface NovaPlannedStep {
  stepId: string;
  type: string;
  requiresApproval: boolean;
}

/**
 * Payload of the per-user `nova:run` socket event the executor emits as a run
 * progresses (run claimed → each step start/end → terminal). Poll-backed UIs use
 * it as a "refetch now" nudge; the payload alone can also drive optimistic UI.
 */
export interface NovaRunProgressEvent {
  runId: string;
  automationId: string;
  status: NovaRunStatus | string;
  step?: {
    stepId: string;
    type: string;
    status: NovaRunStepStatus | string;
  } | null;
  metrics?: Record<string, unknown>;
}
